import { useCallback, useEffect, useRef, useState } from 'react';

import { useAiResult } from './useAiResult';

/**
 * How long a page waits for an AI result before calling it unavailable: the 90 seconds the server counts
 * a pending generation as still running.
 *
 * The two clocks do not start together. The page's starts when it begins waiting — just before it sends
 * a request of its own, or when it finds a generation already running — whereas the server's starts when
 * it records the request. Waiting on its own request, the page gives up a moment early, by the time the
 * request takes to reach the server; waiting on one found running, it may keep waiting up to a full
 * window after the server has written that request off.
 */
export const AI_GENERATION_WAIT_LIMIT_MS = 90_000;

/**
 * Who started the generation being waited on: the user, by a click, or nobody on this page — a
 * generation of theirs already running when the page was opened.
 */
export type AiRequestOrigin = 'user' | 'attached';

/**
 * Why a generation ended without a result: it failed (the request was refused, or the result reported
 * a failure or could not be applied), or no answer came within {@link AI_GENERATION_WAIT_LIMIT_MS} — in
 * which case the result may still have been written.
 */
type AiFailureReason = 'error' | 'timeout';

export type AiGenerationState =
    | { status: 'idle' }
    | { status: 'pending'; origin: AiRequestOrigin; correlationId: string }
    | { status: 'failed'; origin: AiRequestOrigin; reason: AiFailureReason };

const IDLE: AiGenerationState = { status: 'idle' };

interface GenerationTracking {
    subjectId: string | undefined;
    state: AiGenerationState;
    /**
     * The last generation waited on. A refetch naming that same one again — it can outlive the wait by
     * the few milliseconds between the page's clock and the server's — must not restart the wait.
     */
    lastWaitedOn: string | null;
}

const trackingFor = (subjectId: string | undefined): GenerationTracking => ({
    subjectId,
    state: IDLE,
    lastWaitedOn: null,
});

/**
 * The tracking once it follows the subject on screen and the generation the server names for it: reset
 * for another subject, waiting on a generation named for the first time. Pure, and the very same object
 * when nothing changes, so a render can call it on every pass.
 */
const followServer = (
    tracking: GenerationTracking,
    subjectId: string | undefined,
    pendingCorrelationId: string | null | undefined,
): GenerationTracking => {
    const current = tracking.subjectId === subjectId ? tracking : trackingFor(subjectId);
    if (!pendingCorrelationId || current.lastWaitedOn === pendingCorrelationId) {
        return current;
    }

    return {
        ...current,
        lastWaitedOn: pendingCorrelationId,
        state:
            current.state.status === 'pending'
                ? current.state
                : { status: 'pending', origin: 'attached', correlationId: pendingCorrelationId },
    };
};

interface UseAiGenerationParams {
    /** What the generation is for, e.g. a meeting id. Changing it forgets the previous generation;
     * nothing is requested while it is undefined. */
    subjectId: string | undefined;
    /** A generation of the caller's that the server reports as already running for the subject, to
     * wait on instead of starting another. */
    pendingCorrelationId?: string | null;
    /** Sends the request and resolves to the generation actually running: the caller's own
     * correlation id, or the one the server attached it to. */
    request: (subjectId: string, correlationId: string) => Promise<{ correlationId: string }>;
    /** Applies a successful result, when there is anything to apply. Throwing reports the generation
     * as failed. */
    onResult?: (subjectId: string, content: string | undefined) => void;
    /** Called when the wait gives up: the result may still have been written server-side, and the
     * server's copy says so better than the page can. */
    onGiveUp?: (subjectId: string) => void;
}

/**
 * Asks the server for an AI generation and waits for its result over SignalR, in three states: idle,
 * pending, failed. A wait that outlasts {@link AI_GENERATION_WAIT_LIMIT_MS} ends as a failure, never as
 * an endless skeleton. The caller decides what a result changes and how a failure reads on screen.
 */
export const useAiGeneration = ({
    subjectId,
    pendingCorrelationId,
    request,
    onResult,
    onGiveUp,
}: UseAiGenerationParams) => {
    const [storedTracking, setTracking] = useState<GenerationTracking>(() =>
        followServer(trackingFor(subjectId), subjectId, pendingCorrelationId),
    );
    // Followed during the render, never in an effect: an effect runs after the paint, so the render the
    // server's answer arrives on would show the result as unavailable, with a retry to click, for one
    // frame.
    const tracking = followServer(storedTracking, subjectId, pendingCorrelationId);
    if (tracking !== storedTracking) {
        setTracking(tracking);
    }
    const { state } = tracking;

    const result = useAiResult(state.status === 'pending' ? state.correlationId : null);
    // The callbacks are read at the moment a result or a timeout lands, not when the effect that waits
    // for it was created: a caller's closures are new on every render.
    const callbacks = useRef({ request, onResult, onGiveUp });
    useEffect(() => {
        callbacks.current = { request, onResult, onGiveUp };
    });

    /** Ends the wait on `correlationId` with `next`, unless another wait has replaced it meanwhile. */
    const settle = useCallback(
        (correlationId: string, next: AiGenerationState) =>
            setTracking((current) =>
                current.state.status === 'pending' && current.state.correlationId === correlationId
                    ? { ...current, state: next }
                    : current,
            ),
        [],
    );

    useEffect(() => {
        if (
            !subjectId ||
            state.status !== 'pending' ||
            result?.correlationId !== state.correlationId
        ) {
            return;
        }

        const { correlationId, origin } = state;
        const failed: AiGenerationState = { status: 'failed', origin, reason: 'error' };
        if (!result.success) {
            settle(correlationId, failed);
            return;
        }

        try {
            callbacks.current.onResult?.(subjectId, result.content ?? undefined);
            settle(correlationId, IDLE);
        } catch {
            // A result that cannot be applied is reported like any other failure: the page offers to try
            // again, which is the only useful thing left to do with it.
            settle(correlationId, failed);
        }
    }, [result, state, subjectId, settle, callbacks]);

    useEffect(() => {
        if (state.status !== 'pending' || !subjectId) {
            return;
        }

        const { correlationId, origin } = state;
        const timeout = window.setTimeout(() => {
            settle(correlationId, { status: 'failed', origin, reason: 'timeout' });
            callbacks.current.onGiveUp?.(subjectId);
        }, AI_GENERATION_WAIT_LIMIT_MS);

        return () => window.clearTimeout(timeout);
    }, [state, subjectId, settle, callbacks]);

    const generate = useCallback(async () => {
        if (!subjectId) {
            return;
        }

        const ownId = crypto.randomUUID();
        setTracking((current) => ({
            ...current,
            lastWaitedOn: ownId,
            state: { status: 'pending', origin: 'user', correlationId: ownId },
        }));

        try {
            // The server may answer with the id of a generation this user already has running for the
            // subject, in which case that is the one whose result will come.
            const { correlationId: runningId } = await callbacks.current.request(subjectId, ownId);
            if (runningId === ownId) {
                return;
            }

            setTracking((current) =>
                current.state.status === 'pending' && current.state.correlationId === ownId
                    ? {
                          ...current,
                          lastWaitedOn: runningId,
                          state: { status: 'pending', origin: 'user', correlationId: runningId },
                      }
                    : current,
            );
        } catch {
            settle(ownId, { status: 'failed', origin: 'user', reason: 'error' });
        }
    }, [subjectId, settle, callbacks]);

    return { state, generate };
};
